import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/common";
import { ImageUpload } from "@/components/ImageUpload";
import AuthShell from "./AuthShell";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

export default function Register() {
  const { role = "student" } = useParams();
  const isTutor = role === "tutor";
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", education_level: "", subject: "",
    specialization: "", qualification: "", experience: 0, bio: "", profile_picture: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { ...f, role, experience: Number(f.experience) || 0 });
      setDone(data);
      toast.success("Registration successful");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Check your inbox" subtitle="Your account was created.">
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="register-success">
          <CheckCircle2 className="h-10 w-10 text-[#3CC34A]" />
          <p className="text-slate-600">{done.message}</p>
          {done.demo_verification_link && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Demo Mode</p>
              <p className="mt-1 text-amber-900">Email delivery may be unavailable in demo. Use this link to verify:</p>
              <Link to={done.demo_verification_link} className="mt-2 inline-block font-semibold text-[#1F6FFF] underline" data-testid="demo-verification-link">Verify my email now</Link>
            </div>
          )}
          <Link to={`/login/${role}`} className="inline-block font-semibold text-[#1F6FFF]" data-testid="register-go-login">Go to login</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={isTutor ? "Apply as a tutor" : "Create student account"}
      subtitle={isTutor ? "Share your expertise. Admins review every application." : "Find tutors and join live classes."}
      footer={<p>Already registered? <Link to={`/login/${role}`} className="font-semibold text-[#1F6FFF]" data-testid="register-login-link">Log in</Link></p>}>
      <div className="mb-6 grid grid-cols-2 rounded-full bg-white p-1 text-sm font-semibold">
        {["student", "tutor"].map((r) => (
          <Link key={r} to={`/register/${r}`} data-testid={`register-tab-${r}`}
            className={`rounded-full py-2 text-center capitalize transition-colors ${role === r ? "bg-[#0B1A33] text-white" : "text-slate-500"}`}>{r}</Link>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <ImageUpload value={f.profile_picture} name={f.name} onChange={(v) => setF({ ...f, profile_picture: v })} />
        <Field label="Full name"><Input required value={f.name} onChange={set("name")} className="bg-white" data-testid="register-name-input" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email"><Input required type="email" value={f.email} onChange={set("email")} className="bg-white" data-testid="register-email-input" /></Field>
          <Field label="Phone"><Input value={f.phone} onChange={set("phone")} className="bg-white" data-testid="register-phone-input" /></Field>
        </div>
        <Field label="Password (min 8 characters)"><Input required minLength={8} type="password" value={f.password} onChange={set("password")} className="bg-white" data-testid="register-password-input" /></Field>
        {isTutor ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Subject"><Input required value={f.subject} onChange={set("subject")} className="bg-white" data-testid="register-subject-input" /></Field>
              <Field label="Specialization"><Input value={f.specialization} onChange={set("specialization")} className="bg-white" data-testid="register-specialization-input" /></Field>
              <Field label="Qualification"><Input value={f.qualification} onChange={set("qualification")} className="bg-white" data-testid="register-qualification-input" /></Field>
              <Field label="Experience (years)"><Input type="number" min={0} max={60} value={f.experience} onChange={set("experience")} className="bg-white" data-testid="register-experience-input" /></Field>
            </div>
            <Field label="Bio"><Textarea value={f.bio} onChange={set("bio")} maxLength={1500} className="bg-white" data-testid="register-bio-input" /></Field>
          </>
        ) : (
          <Field label="Education level"><Input placeholder="e.g. Grade 11, Undergraduate" value={f.education_level} onChange={set("education_level")} className="bg-white" data-testid="register-education-input" /></Field>
        )}
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-[#3CC34A] font-bold text-[#0B1A33] hover:bg-[#2DA638]" data-testid="register-submit-button">
          {busy && <Spinner />} Create account
        </Button>
      </form>
    </AuthShell>
  );
}
